<script setup lang="ts">
import { ref, computed, watch, onMounted, onUnmounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { RouterLink } from "vue-router";
import ProfileIcon from "@/shared/icons/ProfileIcon.vue";
import CartIcon from "@/shared/icons/CartIcon.vue";
import HomeIcon from "@/shared/icons/HomeIcon.vue";
import { useUserStore } from '@/entities/user/model/userStore';
import { storeToRefs } from 'pinia';
import VKIcon from "@/shared/icons/VKIcon.vue";
import PaymentIcon from "@/shared/icons/PaymentIcon.vue";
import PreorderIcon from "@/shared/icons/PreorderIcon.vue";
import { useCartStore } from '@/pages/Cart/model/useCart';
import AuthModal from '@/features/modals/AuthModal.vue'
import { useModalStore } from '@/features/modals/modalStore'
import ConfirmationModal from '@/features/modals/ConfirmationModal.vue'
import LoaderModal from '@/features/modals/LoaderModal.vue'
import SettingsModal from '@/features/modals/SettingsModal.vue'
import SearchInput from '@/shared/ui/Search/SearchInput.vue'
import { useSearch } from '@/shared/ui/Search/useSearch'
import GiftIcon from '@/shared/icons/GiftIcon.vue'
import Snowfall from '@/shared/ui/Snowfall/Snowfall.vue'
import { useSecretSantaStore } from '@/entities/secretSanta/model/secretSantaStore'
const searchStore = useSearch();
const results = computed<SearchResult[]>(() =>
  searchStore.results.map(r => ({
    id: r.entity_id,
    type: r.type,
    title: r.title,
    imageUrl: r.image_url,
  }))
);

function onSearch(query: string) {
  searchStore.fetchResult(query);
}

function onSelect(result: SearchResult) {
  if (result.type == "fandom") {
    router.push({ path: '/fandom', query: { n: result.title } });
  } else if (result.type == "preorder") {
    router.push(`/preorders/n${result.id}`);
  } else if (result.type == "merch") {
    router.push(`/product/${result.id}`);
  }
}

function onShowAll(query: string) {
  router.push({ path: '/search', query: { query } });
}

function onClear() {
  search.results = [];
}

const open = ref(false);
const mobileProfileOpen = ref(false);
const userStore = useUserStore();
const cartStore = useCartStore();
const { isAuth, avatar } = storeToRefs(userStore);
const route = useRoute();
const router = useRouter();
const modalStore = useModalStore();
const santaStore = useSecretSantaStore();

const showSanta = computed(() => santaStore.isFilled);

const totalCartItems = computed(() => {
  return cartStore.cartData.reduce((total, preorder) => {
    return total + preorder.items.reduce((sum, item) => sum + item.amount, 0);
  }, 0);
});

const activeRoute = computed(() => {
  if (route.path === '/') return 1;
  if (route.path === '/preorders') return 2;
  if (route.path === '/cart') return 3;
  if (route.path === '/orders') return 4;
  return 0;
});

type NavSlot = 'home' | 'preorders' | 'santa' | 'cart' | 'profile';

const navSlots = computed<NavSlot[]>(() =>
  showSanta.value
    ? ['home', 'preorders', 'santa', 'cart', 'profile']
    : ['home', 'preorders', 'cart', 'profile']
);

const activeSlot = computed<NavSlot | null>(() => {
  if (route.name === 'santa') return showSanta.value ? 'santa' : null;
  if (mobileProfileOpen.value) return 'profile';
  const byRoute: (NavSlot | null)[] = [null, 'home', 'preorders', 'cart', 'profile'];
  return byRoute[activeRoute.value];
});

const indicatorStyle = computed(() => {
  const index = activeSlot.value ? navSlots.value.indexOf(activeSlot.value) : -1;
  if (index < 0) {
    return { left: 'calc(50% - 30px)', top: '100px' };
  }
  const p = (2 * index + 1) / (2 * navSlots.value.length);
  return { left: `calc(${p * 100}% - ${30 - 5 * (1 - 2 * p)}px)`, top: '-15px' };
});

async function initSecretSanta() {
  if (!isAuth.value) {
    santaStore.reset();
    return;
  }
  await santaStore.load(true);
  santaStore.startPolling();
}


const handleClickOutside = (event: MouseEvent) => {
  const profileEl = document.querySelector('.profile');
  if (profileEl && !profileEl.contains(event.target as Node)) {
    open.value = false;
  }
};

const handleMobileClickOutside = (event: MouseEvent) => {
  const mobileProfileEl = document.querySelector('.mobile-profile');
  if (mobileProfileEl && !mobileProfileEl.contains(event.target as Node)) {
    mobileProfileOpen.value = false;
  }
};

onMounted(() => {
  document.addEventListener('click', handleClickOutside);
  document.addEventListener('click', handleMobileClickOutside);
  initSecretSanta();
});

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside);
  document.removeEventListener('click', handleMobileClickOutside);
  santaStore.stopPolling();
});

watch(() => route.name, (name) => {
  if (name === 'orders' || name === 'santa') santaStore.refreshUnread();
});

watch(isAuth, (newValue) => {
  initSecretSanta();
  if (newValue) {
    if (modalStore.modal.type == 'auth') {
      closeModalSuccess();
    }
    mobileProfileOpen.value = false;
  }
});

const handleProfileClick = () => {
  if (isAuth.value) {
    router.push('/orders');
  } else {
    modalStore.open('auth');
  }
};

const handleMobileProfileClick = () => {
  if (isAuth.value) {
    router.push('/orders');
  } else {
    modalStore.open('auth');
  }
};

const isAdvancedSearchPage = computed(() => route.name === 'search');

const closeModalSuccess = () => {
  modalStore.close(1500);
}
</script>

<template>
  <SearchInput
    v-if="!isAdvancedSearchPage"
    class="search-mobile"
    style="height: 64px"
    placeholder="Поиск"
    :results="results"
    :loading="searchStore.loading"
    @search="onSearch"
    @select="onSelect"
    @showAll="onShowAll"
    @clear="onClear"
  />
  <header>
    <nav class="desktop-nav">
      <RouterLink class="nav-item" to="/">Главная</RouterLink>
      <RouterLink class="nav-item" to="/preorders">Предзаказы</RouterLink>
      <RouterLink class="nav-item" to="/faq">Вопрос/ответ</RouterLink>
    </nav>

    <div class="desktop-profile">
      <a
        href="https://vk.com/lavka.chinese?from=groups"
        target="_blank"
        class="external-link"
        aria-label="Группа ВКонтакте"
      >
        <VKIcon class="external-icon" />
        ВК
      </a>

      <a
        href="https://vk.com/lavka.chinese?w=donut_payment-214661495&levelId=522"
        target="_blank"
        class="external-link"
        aria-label="Оплата"
      >
        <PaymentIcon class="external-icon" />
        Подписка
      </a>

      <div style="display: flex; align-items: center;">
        <SearchInput
          style="height: 36px; width: 180px"
          placeholder="Поиск"
          :results="results"
          :loading="searchStore.loading"
          @search="onSearch"
          @select="onSelect"
          @showAll="onShowAll"
          @clear="onClear"
        />
      </div>

      <RouterLink to="/cart" class="cart-link">
        <div class="cart-icon-wrapper">
          <CartIcon class="cart-icon"/>
          <span v-if="totalCartItems > 0" class="cart-badge">{{ totalCartItems }}</span>
        </div>
      </RouterLink>
      <div class="profile">
        <button
          class="profile__button"
          aria-label="Профиль"
          @click="handleProfileClick"
        >
          <img
            v-if="isAuth && avatar"
            :src="avatar"
            alt="Аватар пользователя"
            class="avatar"
          >
          <ProfileIcon v-else/>
          <span v-if="santaStore.unreadTotal > 0" class="cart-badge profile-badge">{{ santaStore.unreadTotal }}</span>
        </button>
      </div>
    </div>

    <div class="mobile-nav" :class="{ 'mobile-nav--santa': showSanta }">
      <RouterLink
        to="/"
        class="mobile-nav__item"
        :class="{ active: activeSlot === 'home' }"
        @click="mobileProfileOpen = false"
      >
        <HomeIcon class="mobile-nav__icon"/>
      </RouterLink>

      <RouterLink
        to="/preorders"
        class="mobile-nav__item"
        :class="{ active: activeSlot === 'preorders' }"
        @click="mobileProfileOpen = false"
      >
        <PreorderIcon class="mobile-nav__icon"/>
      </RouterLink>

      <transition name="santa-pop">
        <RouterLink
          v-if="showSanta"
          to="/santa"
          class="mobile-nav__item mobile-nav__santa"
          :class="{ active: activeSlot === 'santa' }"
          aria-label="Тайный Санта"
          @click="mobileProfileOpen = false"
        >
          <div class="mobile-santa-wrapper" :class="{ 'has-unread': santaStore.unreadTotal > 0 }">
            <GiftIcon class="mobile-nav__icon"/>
            <span v-if="santaStore.unreadTotal > 0" class="mobile-cart-badge">{{ santaStore.unreadTotal }}</span>
          </div>
        </RouterLink>
      </transition>

      <RouterLink
        to="/cart"
        class="mobile-nav__item"
        :class="{ active: activeSlot === 'cart' }"
        @click="mobileProfileOpen = false"
      >
        <div class="mobile-cart-icon-wrapper">
          <CartIcon class="mobile-nav__icon"/>
          <span v-if="totalCartItems > 0" class="mobile-cart-badge">{{ totalCartItems }}</span>
        </div>
      </RouterLink>

      <div class="mobile-profile">
        <button
          class="mobile-nav__item"
          :class="{ active: activeSlot === 'profile' }"
          aria-label="Профиль"
          @click="handleMobileProfileClick"
        >
          <img
            v-if="isAuth && avatar"
            :src="avatar"
            alt="Аватар пользователя"
            class="user-avatar mobile-nav__icon"
          />
          <ProfileIcon class="mobile-nav__icon" v-else/>
        </button>
      </div>

      <div class="circle" :style="indicatorStyle"></div>

      <div class="mobile-nav__indicator" :style="indicatorStyle"></div>
    </div>

    <transition name="fade">
      <AuthModal
        v-if="modalStore.modal.type == 'auth'"
        @close="modalStore.close"
        @success="closeModalSuccess"
      />
    </transition>
    <transition name="fade">
      <ConfirmationModal
        v-if="modalStore.modal.type == 'confirm'"
        @close="modalStore.close"
      />
    </transition>
    <transition name="fade">
      <LoaderModal
        v-if="modalStore.modal.type == 'loader'"
      />
    </transition>
    <Snowfall :active="route.name === 'santa'" />
    <transition name="fade">
      <SettingsModal
        v-if="modalStore.modal.type == 'settings'"
        @close="modalStore.close"
      />
    </transition>
  </header>
</template>

<style lang="scss" scoped>
header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20%;
  position: relative;
}

.desktop-nav {
  display: flex;
  gap: 16px;

  a {
    padding: 16px 32px;
    font-weight: 500;
    font-size: 20px;
  }
}

.desktop-profile {
  display: flex;
  align-items: center;
  gap: 20px;
}

.cart-link {
  display: flex;
  align-items: center;
}

.cart-icon {
  width: 32px;
  height: 32px;
}

.cart-icon-wrapper {
  position: relative;
  display: flex;
  align-items: center;
}

.cart-badge {
  position: absolute;
  top: -8px;
  right: -8px;
  background-color: var(--purple);
  color: white;
  border-radius: 50%;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: bold;
}

.profile {
  position: relative;
}

.profile-badge {
  top: 0;
  right: 0;
  background-color: #e53935;
  line-height: 1;
  padding-bottom: 1px;
  font-variant-numeric: tabular-nums;
}

.mobile-nav--santa {
  > .mobile-nav__item,
  > .mobile-profile {
    width: 20%;
  }
}

.mobile-nav > .mobile-nav__item,
.mobile-nav > .mobile-profile {
  transition: width 0.3s ease;
}

.mobile-santa-wrapper {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;

  &.has-unread {
    animation: santa-wiggle 2.4s ease-in-out infinite;
  }

  .mobile-cart-badge {
    background-color: #e53935;
    line-height: 1;
    padding-bottom: 1px;
    font-variant-numeric: tabular-nums;
  }
}

.mobile-nav__santa.active .mobile-cart-badge {
  transform: translateY(-20px);
}

.santa-pop-enter-active {
  transition:
    width 0.3s ease,
    opacity 0.3s ease,
    transform 0.7s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.santa-pop-leave-active {
  transition:
    width 0.3s ease,
    opacity 0.2s ease,
    transform 0.3s ease;
}

.santa-pop-enter-from,
.santa-pop-leave-to {
  width: 0 !important;
  opacity: 0;
  transform: scale(0) rotate(-180deg);
}

@keyframes santa-wiggle {
  0%, 70%, 100% { transform: rotate(0); }
  75% { transform: rotate(-14deg); }
  80% { transform: rotate(12deg); }
  85% { transform: rotate(-8deg); }
  90% { transform: rotate(5deg); }
}

.profile__button {
  position: relative;
  width: 56px;
  height: 56px;
  padding: 8px;
  border: none;
  background: transparent;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;

  svg {
    width: 72%;
    height: 72%;
  }

  &:hover {
    background: #f0f0f0;
  }
}

.avatar {
  width: 100%;
  height: 100%;
  border-radius: 50%;
  object-fit: cover;
}

.external-link {
  display: flex;
  align-items: center;
  padding: 8px;
  border-radius: 8px;
  transition: background 0.3s ease;
  gap: 8px;
  font-weight: 600;
  border: 1px solid var(--black);

  &:hover {
    background: #f0f0f0;
  }
}

.external-icon {
  width: 24px;
  height: 24px;
  color: #333;
}

.search-mobile {
  padding-top: 12px;
  padding-left: 12px;
  padding-right: 12px;
  padding-bottom: 12px;
  display: none;
  width: 100%;
}

.mobile-nav {
  display: none;
  position: fixed;
  bottom: -1.4%;
  left: 0;
  right: 0;
  height: 70px;
  background: rgba(255, 255, 255, 0.7);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.3);
  box-shadow:
    0 4px 6px rgba(0, 0, 0, 0.02),
    inset 0 0 0 1px rgba(255, 255, 255, 0.1);
  z-index: 1000;
  justify-content: space-around;
  align-items: center;
  padding: 0 5px;

  &::before {
    content: '';
    position: absolute;
    width: 84%;
    height: 0;
    bottom: -10px;
    left: 0;
    right: 0;
    margin: auto;
  }

  &__item {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 25%;
    height: 70px;
    position: relative;
    z-index: 2;
    background: transparent;
    border: none;
    cursor: pointer;
    padding: 0;

    &.active {
      .mobile-nav__icon {
        transform: translateY(-20px);
      }

      .mobile-cart-icon-wrapper {
        transform: translateY(1px) translateX(-2px);
      }

      .mobile-cart-badge {
        transform: translateY(-18px);
      }
    }
  }

  &__icon {
    width: 28px;
    height: 28px;
    transition: transform 0.3s cubic-bezier(0.14, -0.08, 0.74, 1.4);
    z-index: 3;
    fill: none;

    svg {
      width: 100%;
      height: 100%;
      display: block;
    }
  }

  &__indicator {
    width: 70px;
    height: 70px;
    border-radius: 50%;
    position: absolute;
    top: -42px;
    transition: left 0.3s cubic-bezier(0.14, -0.08, 0.74, 1.4);
    z-index: 0;
    filter: blur(3px);
  }
}

.mobile-cart-icon-wrapper {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.3s cubic-bezier(0.14, -0.08, 0.74, 1.4);
}

.mobile-cart-badge {
  position: absolute;
  top: -5px;
  right: -5px;
  background-color: var(--purple);
  color: white;
  border-radius: 50%;
  width: 18px;
  height: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  font-weight: bold;
  z-index: 3;
  transition: transform 0.3s cubic-bezier(0.14, -0.08, 0.74, 1.4);
}

.circle {
  width: 60px;
  height: 60px;
  background: rgba(255, 255, 255, 0.4);
  backdrop-filter: blur(600px);
  border: 1px solid rgba(255, 255, 255, 0.3);
  box-shadow:
    0 4px 6px rgba(0, 0, 0, 0.02),
    inset 0 0 0 1px rgba(255, 255, 255, 0.1);
  position: absolute;
  top: -30px;
  z-index: 1;
  border-radius: 50%;
  transition: left 0.3s cubic-bezier(0.14, -0.08, 0.74, 1.4), top 0.3s cubic-bezier(0.14, -0.08, 0.74, 1.4);
}

.mobile-profile {
  position: relative;
  width: 25%;
  height: 70px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.user-avatar {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  object-fit: cover;
}

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 2000;
}

.modal-container {
  position: relative;
  width: 100%;
  max-width: 400px;
}

.modal-content-wrapper {
  position: relative;
}

.modal-content {
  background: white;
  border-radius: 12px;
  padding: 24px;
  width: 100%;
  position: relative;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  animation: modal-appear 0.3s ease-out;
  z-index: 5;
  margin: 10px auto;
}

@media (max-width: 480px) {
  .modal-content {
    width: 95%;
    padding: 20px 15px;
  }
}

.modal-close {
  position: absolute;
  top: 12px;
  right: 12px;
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: #666;
  padding: 4px;
  line-height: 1;

  &:hover {
    color: #333;
  }
}

.modal-title {
  margin-top: 0;
  margin-bottom: 20px;
  text-align: center;
  color: #333;
}

@keyframes modal-appear {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

@media (max-width: 768px) {
  .desktop-nav,
  .desktop-profile {
    display: none;
  }

  .mobile-nav,
  .search-mobile {
    display: flex;
  }
}

@media (min-width: 769px) {
  .mobile-nav,
  .search-mobile {
    display: none;
  }
}

.nav-item {
  display: flex;
  align-items: center;
}
</style>
